/**
 * How an agreement's `status` is rendered — one definition, every surface.
 *
 * Extracted from the Agreements table on 2026-08-09 when the Hotels and
 * Customers panels started listing agreements too. Three copies of this map
 * would drift into three different colours for `negotiating`, and the colour is
 * the whole point: the team reads the queue by it.
 *
 * `status` is the STORED value (see AGREEMENT_STATUSES in the API's
 * lib/hsp/agreement.ts). It is deliberately not the derived five-stage model —
 * those stages exist only as server-side query filters, and re-deriving them in
 * the browser would be a second implementation of a rule that is meant to have
 * exactly one.
 */

/** status → the badge classes that colour it. */
export const STATUS_BADGES: Record<string, string> = {
  rfp_created: 'badge',
  rfp_sent: 'badge badge-amber',
  // BLUE, because the ball is ours. The amber statuses are the ones where we
  // are waiting on the hotel, and the whole point of the rename is that the two
  // are distinguishable at a glance — "sent means it was sent to the hotel…
  // pending acceptance means it's waiting on us."
  rfp_pending_acceptance: 'badge badge-blue',
  rfp_accepted: 'badge badge-green',
  // Amber like the other "waiting on the hotel" states: a rebid has been asked
  // for and the hotel owes the answer.
  rebid_requested: 'badge badge-amber',
  loading: 'badge badge-blue',
  active: 'badge badge-green',
  declined: 'badge badge-red',
  dismissed: 'badge',
  expired: 'badge badge-red',
  terminated: 'badge badge-red',
};

/**
 * Badge classes for a status, falling back to the neutral badge. An unknown
 * status still renders as a badge rather than as unstyled text — a new status
 * added server-side should look plain, not broken.
 */
export function statusBadge(status: string): string {
  return STATUS_BADGES[status] || 'badge';
}

/**
 * The words the team uses, which underscore-replacement cannot produce.
 *
 * `rfp_pending_acceptance` reads as "rfp pending acceptance" under the old
 * fallback — the prefix convention Jeff asked for ("RFPX, RFPY, RFPZ") only
 * works if the prefix is actually rendered as RFP. Stacie chose the wording:
 * "that's how we're used to seeing it."
 */
export const STATUS_LABELS: Record<string, string> = {
  rfp_created: 'RFP — created',
  rfp_sent: 'RFP — sent to hotel',
  rfp_pending_acceptance: 'RFP — pending acceptance',
  rfp_accepted: 'RFP — accepted',
  rebid_requested: 'Rebid requested',
};

/**
 * A status as the team says it. Falls back to underscore-replacement so a
 * status added server-side renders readably rather than blank — the same
 * degrade-don't-break rule `statusBadge` follows.
 */
export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/**
 * The statuses that CLOSE an agreement.
 *
 * Mirrors the API's `TERMINAL_STATUSES`, and the duplication is deliberate and
 * bounded: this drives COPY and AFFORDANCE — "Close agreement" rather than
 * "Change status", a reason box that is recommended rather than optional, a
 * warning that the move cannot be undone. It does NOT decide legality. Which
 * moves are offered still comes from the server's `allowed_transitions`, so a
 * client that is wrong here shows the wrong button label, never an illegal
 * move. That is the line the Agreements stage model draws too: rules on the
 * server, presentation here.
 */
export const CLOSING_STATUSES: ReadonlySet<string> = new Set([
  'declined',
  'dismissed',
  'expired',
  'terminated',
]);

/** Whether moving to `status` ends the agreement for good. */
export function closesTheAgreement(status: string): boolean {
  return CLOSING_STATUSES.has(status);
}
